import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Swords, Home as HomeIcon, Trophy, SkipForward } from "lucide-react";
import { apiClient, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import MathText from "../components/MathText";
import ProtectedQuestion from "../components/ProtectedQuestion";
import AntiCheatPopup from "../components/AntiCheatPopup";
import useAntiCheat from "../hooks/useAntiCheat";
import UserAvatar from "../components/UserAvatar";
import MatchTools from "../components/MatchTools";
import IncorrectFlash from "../components/IncorrectFlash";

/**
 * Private bracket match — polling-based 1v1.
 *
 * Backend contract:
 *   GET  /private-match/{id}/state → current question + scores + timer
 *   POST /private-match/{id}/answer { answer }
 * Polls every 1.1s while playing.
 */
export default function PrivateMatch() {
  const { matchId } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const { user } = useAuth();
  const lobbyCode = loc?.state?.code;
  const [state, setState] = useState(null);
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [popup, setPopup] = useState(null); // { violation, message }
  const pollRef = useRef(null);
  const [wrongTrigger, setWrongTrigger] = useState(0);
  const [skipping, setSkipping] = useState(false);
  const statusRef = useRef(null);
  const participantRef = useRef(false);
  const forfeitedRef = useRef(false);

  const fetchState = useCallback(async () => {
    try {
      const r = await apiClient.get(`/private-match/${matchId}/state`);
      setState(r.data);
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error("Match not found");
        nav(lobbyCode ? `/private/${lobbyCode}` : "/private");
      }
    }
  }, [matchId, nav, lobbyCode]);

  useEffect(() => { fetchState(); }, [fetchState]);

  // Poll while playing
  useEffect(() => {
    if (!state || state.status === "finished") {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(fetchState, 1100);
    return () => { if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null; };
  }, [state, fetchState]);

  // Anti-cheat with popup feedback
  const antiCheat = useAntiCheat({
    enabled: state?.status === "playing" && state?.is_participant && !state?.i_already_answered && !state?.round_winner,
    roundKey: `${state?.current ?? -1}`,
    onForfeit: () => {
      // forfeit this round: submit a deliberately wrong answer
      setPopup({ violation: "forfeit" });
      apiClient.post(`/private-match/${matchId}/answer`, { answer: "__FORFEIT__" }).catch(() => {});
    },
    warnText: "Cannot switch tabs",
  });

  // Surface a popup the first time the user switches tabs during a round.
  useEffect(() => {
    if (antiCheat.warning) {
      setPopup({ violation: "tab_switch" });
    }
  }, [antiCheat.warning, antiCheat.blurCount]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!answer.trim() || submitting) return;
    setSubmitting(true);
    try {
      const r = await apiClient.post(`/private-match/${matchId}/answer`, { answer });
      if (r.data?.ok && !r.data?.correct) {
        setWrongTrigger((n) => n + 1);
        toast.error("Incorrect — try again");
      }
      setAnswer("");
      fetchState();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
    } finally { setSubmitting(false); }
  };

  // Track latest status so the forfeit-on-leave handlers read fresh values.
  useEffect(() => {
    statusRef.current = state?.status;
    participantRef.current = !!state?.is_participant;
  }, [state]);

  // Leaving an in-progress private match = forfeit (remaining player wins).
  const doForfeit = useCallback((keepalive) => {
    if (forfeitedRef.current) return;
    if (statusRef.current !== "playing" || !participantRef.current) return;
    forfeitedRef.current = true;
    const token = localStorage.getItem("ba_token");
    try {
      fetch(`${API}/private-match/${matchId}/forfeit`, {
        method: "POST",
        keepalive: !!keepalive,
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (_) { /* ignore */ }
  }, [matchId]);

  useEffect(() => {
    const onHide = () => doForfeit(true);
    window.addEventListener("pagehide", onHide);
    window.addEventListener("beforeunload", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("beforeunload", onHide);
      doForfeit(false); // SPA navigation away mid-match counts as a forfeit
    };
  }, [doForfeit]);

  const skip = async () => {
    if (skipping || state?.i_already_answered) return;
    setSkipping(true);
    try {
      await apiClient.post(`/private-match/${matchId}/skip`);
      setAnswer("");
      fetchState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not skip");
    } finally {
      setTimeout(() => setSkipping(false), 1200); // cooldown to prevent abuse
    }
  };

  if (!state) {
    return (
      <div className="min-h-[80vh] grid place-items-center">
        <Loader2 className="w-10 h-10 text-blue animate-spin" />
      </div>
    );
  }

  if (state.status === "finished") {
    const winner = state.winner_username;
    const isWinner = winner && winner === user?.username;
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="sm-card p-10 text-center">
          <Trophy className={`w-14 h-14 mx-auto ${isWinner ? "text-yellow-400" : "text-soft"}`} />
          <div className="font-mono text-[10px] tracking-[0.45em] text-blue mt-3 uppercase">Match Complete</div>
          <div className="font-heading font-black text-4xl mt-2">
            {winner === "TIE" ? "DRAW" : `${winner} WINS`}
          </div>
          <div className="mt-6 flex items-center justify-center gap-6">
            {(state.players || []).map((p) => (
              <div key={p.id} className="text-center">
                <div className="font-heading font-bold">{p.username}</div>
                <div className="font-mono text-3xl text-blue mt-1">{p.score}</div>
              </div>
            ))}
          </div>
          <button
            onClick={() => nav(lobbyCode ? `/private/${lobbyCode}` : "/private")}
            className="sm-btn-primary px-6 py-3 mt-8 inline-flex items-center gap-2"
            data-testid="pm-back-to-lobby"
          >
            <HomeIcon size={14} /> BACK TO BRACKET
          </button>
        </motion.div>
      </div>
    );
  }

  if (!state.is_participant) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="sm-card p-10 text-center">
          <div className="font-mono text-[10px] tracking-[0.45em] text-soft uppercase">Spectator</div>
          <div className="font-heading font-black text-3xl mt-2">Watching match in progress</div>
          <Scoreboard players={state.players} round={state.current + 1} total={state.total} timer={state.timer_left} />
          {state.question && (
            <div className="sm-card mt-6 p-6 text-left">
              <div className="font-mono text-[10px] tracking-[0.3em] text-blue">{state.question.subject} · {state.question.topic} · {state.question.grade}</div>
              <div className="font-heading font-bold text-xl mt-3 question-text"><ProtectedQuestion>{state.question.question}</ProtectedQuestion></div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const showResult = !!state.round_winner || !!state.round_correct_answer;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <IncorrectFlash trigger={wrongTrigger} />
      {state.status === "playing" && <MatchTools questionKey={state.current} />}
      <AntiCheatPopup
        visible={!!popup}
        violation={popup?.violation}
        message={popup?.message}
        onDismiss={() => setPopup(null)}
      />
      <Scoreboard players={state.players} round={state.current + 1} total={state.total} timer={state.timer_left} me={user?.username} />
      <motion.div
        key={state.current}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        className="sm-card p-6 sm:p-8 mt-4"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="font-mono text-[10px] tracking-[0.3em] text-blue">{state.question?.subject} · {state.question?.topic} · {state.question?.grade}</div>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase px-2 py-1 border border-blue-500/40 text-blue">{state.question?.difficulty || "medium"}</div>
        </div>
        <div className="font-heading font-bold text-2xl sm:text-3xl mt-4 leading-snug max-w-full break-words question-text" data-testid="pm-question-text">
          <ProtectedQuestion>{state.question?.question || ""}</ProtectedQuestion>
        </div>

        <AnimatePresence>
          {showResult && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-5 p-4 rounded-xl border"
              style={{ borderColor: "rgba(59,130,246,0.5)", background: "rgba(59,130,246,0.06)" }}
            >
              <div className="font-mono text-[10px] tracking-[0.3em] text-blue uppercase">{state.round_winner ? `${state.round_winner} took the round ` : "No one answered"}</div>
              <div className="mt-1 text-sm"><span className="text-soft">Answer:</span> <span className="text-white font-bold">{state.round_correct_answer}</span></div>
              {state.round_explanation && <div className="mt-1 text-xs text-soft">{state.round_explanation}</div>}
            </motion.div>
          )}
        </AnimatePresence>

        {!showResult && (
          <>
            <form onSubmit={submit} className="mt-6 flex gap-3">
              <input
                autoFocus
                className="sm-input flex-1 text-lg"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                onPaste={(e) => { e.preventDefault(); setPopup({ violation: "paste" }); toast.error("Pasting is disabled during matches."); }}
                onCopy={(e) => { e.preventDefault(); setPopup({ violation: "copy" }); }}
                onCut={(e) => { e.preventDefault(); setPopup({ violation: "copy" }); }}
                disabled={submitting || state.i_already_answered}
                placeholder={state.i_already_answered ? "Waiting for opponent…" : "Type your answer…"}
                data-testid="pm-answer-input"
              />
              <button
                type="submit"
                className="sm-btn-primary px-5 py-3"
                disabled={submitting || state.i_already_answered || !answer.trim()}
                data-testid="pm-answer-submit"
              >
                {submitting ? "…" : "SUBMIT"}
              </button>
            </form>
            <div className="mt-3 flex items-center justify-between gap-3">
              <div className="font-mono text-[10px] tracking-[0.3em] text-soft">
                {state.i_already_answered ? "WAITING FOR OPPONENT" : "UNLIMITED ATTEMPTS · KEEP TRYING"}
              </div>
              <button
                type="button"
                onClick={skip}
                disabled={skipping || state.i_already_answered}
                className="sm-btn-outline px-4 py-2 text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="pm-skip"
              >
                <SkipForward size={12} /> SKIP
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}

function Scoreboard({ players = [], round, total, timer, me }) {
  return (
    <div className="sm-card p-4 flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-3">
        {players.map((p, i) => (
          <div key={p.id || i} className="flex items-center gap-2">
            <UserAvatar size={28} />
            <div className="text-sm">
              <div className={`font-heading font-bold leading-none ${me === p.username ? "text-blue" : ""}`}>{p.username}</div>
              <div className="font-mono text-[10px] tracking-[0.25em] text-soft mt-1">SCORE {p.score}</div>
            </div>
            {i < players.length - 1 && <Swords size={16} className="text-pink mx-2" />}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4">
        <div className="font-mono text-[10px] tracking-[0.3em] text-soft">ROUND</div>
        <div className="font-heading font-black text-xl">{round} / {total}</div>
        <div className="font-mono text-[10px] tracking-[0.3em] text-soft ml-4">TIMER</div>
        <div className={`font-heading font-black text-xl ${timer <= 10 ? "text-pink" : ""}`}>{timer ?? "—"}s</div>
      </div>
    </div>
  );
}
